import { z } from 'zod';

import { PriceSchema, TotalPricingSchema } from './pricingSchema.ts';

export const PartSchema = z.object({
  partId: z.uuid(),
  description: z.string(),
  fulfilledUnitIds: z.uuid().array(),
});

export const PaymentItemSchema = z.object({
  jobId: z.uuid(),
  itemId: z.uuid(),
  parts: z.array(PartSchema),
});

export const PaymentTypeEnum = z.enum([
  'CustomerPayment',
  'CustomerRefund',
  'Void',
]);

export const TransactionLabelEnum = z.enum([
  'payment',
  'gestureOfGoodwill',
  'refund',
  'void',
  'notImplemented',
]);

export const BsroRefundReasonEnum = z.enum([
  'customerSatisfaction',
  'serviceRecoveryOrPerceivedInjustice',
  'unappliedCoupon',
  'thirtyDaysPriceGuarantee',
]);

export const MyCarRefundReasonEnum = z.enum([
  'partialRefund',
  'goodwillRefund',
  'incorrectlyBilled',
  'incorrectPaymentMethod',
]);

export const RefundReasonEnum = z.union([
  BsroRefundReasonEnum,
  MyCarRefundReasonEnum,
]);

export type TransactionLabelTypes = z.infer<typeof TransactionLabelEnum>;

export const PaymentMethodEnum = z
  .enum([
    'cash',
    'card',
    'eft',
    'giftCard',
    'keyDrop',
    'cfna',
    'link',
    'olp',
    'zip',
    'btf',
  ])
  .or(z.null());

export const QuestPayloadTypeEnum = z
  .enum([
    'LogonResponse',
    'PaymentResponse',
    'RefundResponse',
    'ProblemDetails',
    'Unknown',
  ])
  .or(z.null());

export const QuestStateEnum = z.enum(['Complete', 'Incomplete']).or(z.null());

export const RefundTypeEnum = z.enum(['cash', 'card', 'cfna']);

export const PaymentTransactionJobItemSchema = z.object({
  id: z.uuid(),
  description: z.string().nullish(),
  quantity: z.int(),
  lineAmount: PriceSchema,
});

export const OrderPaymentsTransactionSchema = z.object({
  id: z.uuid(),
  jobItems: z.array(PaymentTransactionJobItemSchema).nullish(),
  paymentMethod: z.string().nullish(),
  cardType: z.string().nullish(),
  chequeNumber: z.string().nullish(),
  chequeAuthorisationCode: z.string().nullish(),
  paymentReference: z.string().nullish(),
  paymentDate: z.coerce.date<Date>(),
  orderReferenceId: z.uuid().nullish(),
  reason: z.string().nullish(),
  reasonCode: z.string().nullish(),
  type: PaymentTypeEnum,
  originalPaymentTransactionId: z.uuid().nullish(),
  amount: PriceSchema,
  traceData: z.record(z.union([z.string(), z.number()]), z.unknown()).nullish(),
  isRefundable: z.boolean().nullish(),
  voidable: z.boolean().nullish(),
});

export const PaymentTransactionSchema = OrderPaymentsTransactionSchema.omit({
  jobItems: true,
  paymentMethod: true,
  paymentDate: true,
  traceData: true,
}).extend({
  jobItems: PaymentTransactionJobItemSchema.array(),
  paymentMethod: PaymentMethodEnum,
  paymentDate: z.coerce.date<Date>(),
  traceData: z
    .record(z.union([z.string(), z.number()]), z.unknown())
    .optional(),
  transactionLabel: TransactionLabelEnum,
  voidable: z.boolean(),
  isRefundable: z.boolean().nullish(),
});

export const PaymentsTotalSchema = z.object({
  totalPaid: TotalPricingSchema,
  totalPayable: TotalPricingSchema.omit({ tax: true }),
  totalOutstanding: TotalPricingSchema.omit({ tax: true }),
  totalRefunded: TotalPricingSchema.omit({ tax: true }),
});

export const OrderPaymentsSchema = z.object({
  transactions: PaymentTransactionSchema.array(),
  paymentsTotal: PaymentsTotalSchema,
});

const PaymentCurrentStateSchema = z.object({
  totalPayable: PriceSchema,
  totalPaid: PriceSchema,
  totalOutstanding: PriceSchema,
  totalRefunded: PriceSchema,
});

export const GetOrderPaymentsResponseSchema = z.object({
  currentState: PaymentCurrentStateSchema.nullish(),
  transactions: z.array(OrderPaymentsTransactionSchema).nullish(),
  paymentsTotal: PaymentsTotalSchema,
});

export const QuestCardPaymentRequestSchema = z.object({
  orderId: z.string(),
  amount: z.number(),
  posId: z.string(),
  frontEndId: z.string(),
  serviceLocationId: z.string(),
});

export const QuestRefundRequestSchema = z.object({
  originalOrderId: z.string(),
  paymentId: z.string(),
  totalOrderAmount: z.number(),
  reason: z.string(),
  refundAmount: z.number(),
  posId: z.string(),
  frontEndId: z.string(),
  serviceLocationId: z.string(),
  details: z.string(),
});

export const QuestRefundExistingOrderRequestSchema = z.object({
  originalOrderId: z.string(),
  refundOrderId: z.string(),
  paymentId: z.string(),
  amount: z.number(),
  posId: z.string(),
  frontEndId: z.string(),
  serviceLocationId: z.string(),
});

export const QuestResultStatusEnum = z.enum([
  'Successful',
  'Cancelled',
  'Declined',
  'Failed',
  'Unknown',
  'PowerFailure',
  'InProgress',
]);

export const QuestCardPaymentResponseSchema = z.object({
  orderId: z.string(),
  card: z
    .object({
      type: z.string(),
      maskedNumber: z.string(),
    })
    .optional(),
  resultStatus: QuestResultStatusEnum,
  resultCode: z.string().optional(),
  resultText: z.string(),
});

export const QuestCardRefundResponseSchema = z.object({
  orderId: z.string(),
  card: z
    .object({
      type: z.string(),
      maskedNumber: z.string(),
    })
    .optional(),
  resultStatus: QuestResultStatusEnum,
  resultText: z.string(),
  refund: z.object({
    id: z.string(),
    number: z.string().optional(),
  }),
});

export const QuestProblemDetailsPayloadSchema = z.object({
  title: z.string(),
  status: z.number(),
  detail: z.string(),
});

export const QuestResponseEndpointRequestSchema = z.object({
  orderId: z.string(),
  posId: z.string(),
  frontEndId: z.string(),
  serviceLocationId: z.string(),
});

export const QuestLockedStateResponseSchema = z.object({
  title: z.string(),
  status: z.number(),
  detail: z.string(),
  PosId: z.string(),
});

export const QuestResponseEndpointResponseSchema = z.discriminatedUnion(
  'payloadType',
  [
    z.object({
      payloadType: z.literal('RefundResponse'),
      state: QuestStateEnum,
      payload: QuestCardRefundResponseSchema,
    }),
    z.object({
      payloadType: z.literal('PaymentResponse'),
      state: QuestStateEnum,
      payload: QuestCardPaymentResponseSchema,
    }),
    z.object({
      payloadType: z.literal('ProblemDetails'),
      state: QuestStateEnum,
      payload: QuestProblemDetailsPayloadSchema,
    }),
    z.object({
      payloadType: z.literal('Unknown'),
      state: QuestStateEnum,
      payload: z.unknown(),
    }),
  ]
);

export const MyCarPaymentByLinkSchema = z.object({
  orderId: z.string(),
  orderNumber: z.string(),
  mobileNumber: z.string(),
  currency: z.string(),
  amount: z.number(),
});

export const MyCarPaymentByLinkResponseSchema = z.object({
  paymentLink: z.string(),
});

export const AdyenRefundSchema = z.object({
  orderId: z.string(),
  adyenRefundAmount: z.number(),
  currency: z.string(),
  reason: z.string(),
  totalRefundedAmount: z.number(),
  refundReasonCode: z.string().optional(),
  refundDetails: z.string().optional(),
});

export const AdyenRefundResponseSchema = z.object({
  isSuccess: z.boolean(),
  message: z.string().nullable(),
  refundOrderId: z.string(),
  refundOrderNumber: z.string(),
});

export const AdyenRefundToExistingSchema = z.object({
  orderId: z.string(),
  adyenRefundAmount: z.number(),
  currency: z.string(),
  refundOrderId: z.string(),
  refundReasonCode: z.string().optional(),
  refundDetails: z.string().optional(),
});

export const AdyenRefundToExistingResponseSchema = z.object({
  isSuccess: z.boolean().nullish(),
  message: z.string().nullish(),
  refundOrderId: z.string().nullish(),
  refundOrderNumber: z.string().nullish(),
});

export const ZipRefundSchema = z.object({
  orderId: z.string(),
  zippayRefundAmount: z.number(),
  reason: z.string(),
  totalRefundedAmount: z.number(),
  refundReasonCode: z.string().optional(),
  refundDetails: z.string().optional(),
});

export const ZipRefundResponseSchema = z.object({
  isSuccess: z.boolean(),
  message: z.string().nullable(),
  refundOrderId: z.string().nullable(),
  refundOrderNumber: z.string().nullable(),
});

export const ZipRefundToExistingSchema = z.object({
  orderId: z.string(),
  zippayRefundAmount: z.number(),
  refundOrderId: z.string(),
  refundReasonCode: z.string().optional(),
  refundDetails: z.string().optional(),
});

export const ZipRefundToExistingResponseSchema = z.object({
  isSuccess: z.boolean().nullable(),
  message: z.string().nullable(),
  refundOrderId: z.string().nullable(),
  refundOrderNumber: z.string().nullable(),
});

export const QuestPinPadsResponseSchema = z.array(
  z.object({
    name: z.string(),
    posReference: z.string(),
  })
);

export const CancelQuestPaymentResponseSchema = z.object({
  title: z.string().optional(),
  status: z.number().optional(),
  detail: z.string().optional(),
  posId: z.string().optional(),
});

export const CanProcessPaymentResponseSchema = z.object({
  canProcess: z.boolean(),
  posReference: z.string().nullable(),
  posId: z.string().nullable(),
});

export const PaymentRecoveryV2RequestSchema = z.object({
  posId: z.string(),
  posReference: z.string(),
  serviceLocationId: z.string(),
  frontEndId: z.string(),
});

export const PaymentRecoveryV2ResponseSchema = z.object({
  posId: z.string(),
  sent: z.boolean(),
});

export const CancelQuestPaymentRequestSchema = z.object({
  posId: z.string(),
  frontEndId: z.string(),
  serviceLocationId: z.string(),
});

export const OrderInvoiceCardDetailsSchema = z.object({
  dateTime: z.string().optional(),
  amount: z.string().optional(),
  CardNumber: z.string().optional(),
  CardType: z.string().optional(),
  AID: z.string().optional(),
  RRN: z.string().optional(),
  AuthCode: z.string().optional(),
  RespCode: z.string().optional(),
  RelayMID: z.string().optional(),
  RelayTID: z.string().optional(),
  ATC: z.string().optional(),
  APSN: z.string().optional(),
  ResultCode: z.string().optional(),
  ResultText: z.string().optional(),
});

export const CheckQuestPaymentStatusSchema = z.object({
  orderId: z.string(),
  serviceLocationId: z.string(),
});

export const ZipPayRequestSchema = z.object({
  orderId: z.string(),
  amount: z.number(),
  serviceLocationId: z.string(),
  frontEndId: z.string(),
  inStoreCode: z.string(),
});

export const ZipPayRequestResponseSchema = z.object({
  type: z.string().optional(),
  title: z.string().optional(),
  status: z.number().optional(),
  detail: z.string().optional(),
  traceId: z.string().optional(),
  errorCode: z.string().optional(),
  purchaseRequest: z.string().optional(),
});

export const CheckZipPayStatusResponseSchema = z.object({
  paymentStatus: z.boolean(),
  paymentMessage: z.string().nullable(),
});

// Bank Transfer Payments

export const BankTransferRequestSchema = z.object({
  orderId: z.string().uuid(),
  amount: z.number(),
});

export const BankTransferValidationErrorSchema = z.object({
  title: z.string(),
  status: z.number(),
  traceId: z.string(),
  errors: z.record(z.string(), z.array(z.string())),
});

export const BankTransferResponseSchema = z.union([
  z.boolean(),
  BankTransferValidationErrorSchema,
]);

export type QuestPinPadsResponseModel = z.infer<
  typeof QuestPinPadsResponseSchema
>;

export type MyCarPaymentByLinkResponseModel = z.infer<
  typeof MyCarPaymentByLinkResponseSchema
>;

export type OrderInvoiceCardDetailsModel = z.infer<
  typeof OrderInvoiceCardDetailsSchema
>;

export type MyCarPaymentByLinkModel = z.infer<typeof MyCarPaymentByLinkSchema>;

export type AdyenRefundModel = z.infer<typeof AdyenRefundSchema>;

export type AdyenRefundResponseModel = z.infer<
  typeof AdyenRefundResponseSchema
>;

export type AdyenRefundToExistingModel = z.infer<
  typeof AdyenRefundToExistingSchema
>;

export type AdyenRefundToExistingResponseModel = z.infer<
  typeof AdyenRefundToExistingResponseSchema
>;
export type ZipRefundModel = z.infer<typeof ZipRefundSchema>;

export type ZipRefundResponseModel = z.infer<typeof ZipRefundResponseSchema>;

export type ZipRefundToExistingModel = z.infer<
  typeof ZipRefundToExistingSchema
>;

export type ZipRefundToExistingResponseModel = z.infer<
  typeof ZipRefundToExistingResponseSchema
>;

export const OrderPaymentsRefundValidSchema = PaymentTransactionSchema.array();

export const PaymentPriceSchema = PriceSchema.omit({ taxPercentage: true });

export type OrderPaymentsTransactionModel = z.infer<
  typeof OrderPaymentsTransactionSchema
>;
export type GetOrderPaymentsResponseModel = z.infer<
  typeof GetOrderPaymentsResponseSchema
>;
export type PaymentMethod = z.infer<typeof PaymentMethodEnum>;
export type PaymentTransactionModel = z.infer<typeof PaymentTransactionSchema>;
export type PaymentsTotalModel = z.infer<typeof PaymentsTotalSchema>;
export type OrderPaymentsModel = z.infer<typeof OrderPaymentsSchema>;
export type RefundType = z.infer<typeof RefundTypeEnum>;
export type RefundReason = z.infer<typeof RefundReasonEnum>;

// Quest Payments

export type QuestResponseRequestModel = z.infer<
  typeof QuestResponseEndpointRequestSchema
>;

export type QuestResponseResultModel = z.infer<
  typeof QuestResponseEndpointResponseSchema
>;

export type QuestCardPaymentRequestModel = z.infer<
  typeof QuestCardPaymentRequestSchema
>;

export type QuestRefundRequestModel = z.infer<typeof QuestRefundRequestSchema>;

export type QuestRefundExistingOrderRequestModel = z.infer<
  typeof QuestRefundExistingOrderRequestSchema
>;

export type QuestCardPaymentResponseModel = z.infer<
  typeof QuestCardPaymentResponseSchema
>;

export type QuestProblemDetailsPayloadModel = z.infer<
  typeof QuestProblemDetailsPayloadSchema
>;

export type QuestResultStatus = z.infer<typeof QuestResultStatusEnum>;

export type CancelQuestPaymentResponseModel = z.infer<
  typeof CancelQuestPaymentResponseSchema
>;

export type CanProcessPaymentResponseModel = z.infer<
  typeof CanProcessPaymentResponseSchema
>;
export type PaymentRecoveryV2ResponseModel = z.infer<
  typeof PaymentRecoveryV2ResponseSchema
>;
export type PaymentRecoveryV2RequestModel = z.infer<
  typeof PaymentRecoveryV2RequestSchema
>;

export type CancelQuestPaymentRequestModel = z.infer<
  typeof CancelQuestPaymentRequestSchema
>;

export type CheckQuestPaymentStatusModel = z.infer<
  typeof CheckQuestPaymentStatusSchema
>;

// Zip Pay Payments

export type ZipPayRequestModel = z.infer<typeof ZipPayRequestSchema>;

export type ZipPayRequestResponseModel = z.infer<
  typeof ZipPayRequestResponseSchema
>;

export type CheckZipPayStatusResponseModel = z.infer<
  typeof CheckZipPayStatusResponseSchema
>;

// Bank Transfer Payments

export type BankTransferRequestModel = z.infer<
  typeof BankTransferRequestSchema
>;

export type BankTransferResponseModel = z.infer<
  typeof BankTransferResponseSchema
>;
